import { useState, useEffect, useRef } from "react";
import { Sparkles, Plus, MessageSquare, Trash2, Pencil, Send, Loader2, AlertCircle, User as UserIcon, Bot, Copy, Check, Square, Paperclip, X, FileText, FileSpreadsheet, FileImage, File as FileIcon, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../hooks/useAuth";
import { getAiConfig } from "../services/crmServices/settingService";
import {
    fetchConversations,
    createConversation,
    renameConversation,
    deleteConversation,
    fetchMessages,
    streamMessage,
    fetchUsage,
    uploadAttachments,
    fetchAttachmentBlob
} from "../services/crmServices/aiChatService";

export function AIAssistant() {
    const { user } = useAuth();

    const [aiConfig, setAiConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);

    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);

    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [streamingContent, setStreamingContent] = useState("");
    const [streamingStatus, setStreamingStatus] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editTitle, setEditTitle] = useState("");
    const [usage, setUsage] = useState(null);

    // Attachments staged for the next message
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);

    const streamControllerRef = useRef(null);
    const scrollRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    // Initial load
    useEffect(() => {
        (async () => {
            try {
                const res = await getAiConfig();
                setAiConfig(res?.data || null);
            } catch {
                setAiConfig(null);
            } finally {
                setConfigLoading(false);
            }
            await refreshConversations();
            try {
                const u = await fetchUsage();
                setUsage(u?.data || null);
            } catch { /* ignore */ }
        })();
    }, []);

    const refreshConversations = async () => {
        try {
            const res = await fetchConversations();
            const list = res?.data || [];
            setConversations(list);
            if (list.length > 0 && !activeId) {
                setActiveId(list[0]._id);
            }
        } catch (err) {
            toast.error("Failed to load conversations");
        }
    };

    // Load messages when active conversation changes
    useEffect(() => {
        if (!activeId) {
            setMessages([]);
            return;
        }
        (async () => {
            setLoadingMessages(true);
            try {
                const res = await fetchMessages(activeId);
                setMessages(res?.data?.messages || []);
            } catch {
                toast.error("Failed to load messages");
            } finally {
                setLoadingMessages(false);
            }
        })();
    }, [activeId]);

    // Auto-scroll to bottom on new content
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, streamingContent]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + "px";
        }
    }, [input]);

    const handleNewChat = async () => {
        try {
            const res = await createConversation();
            const conv = res?.data;
            if (conv) {
                setConversations((prev) => [conv, ...prev]);
                setActiveId(conv._id);
                setMessages([]);
            }
        } catch {
            toast.error("Failed to create chat");
        }
    };

    const handleDelete = async (id, e) => {
        e?.stopPropagation();
        if (!window.confirm("Delete this conversation?")) return;
        try {
            await deleteConversation(id);
            setConversations((prev) => prev.filter((c) => c._id !== id));
            if (activeId === id) {
                setActiveId(null);
                setMessages([]);
            }
        } catch {
            toast.error("Failed to delete");
        }
    };

    const handleRename = async (id) => {
        if (!editTitle.trim()) {
            setEditingId(null);
            return;
        }
        try {
            const res = await renameConversation(id, editTitle.trim());
            setConversations((prev) => prev.map((c) => (c._id === id ? { ...c, title: res?.data?.title || editTitle.trim() } : c)));
        } catch {
            toast.error("Failed to rename");
        } finally {
            setEditingId(null);
            setEditTitle("");
        }
    };

    const ensureConversation = async () => {
        if (activeId) return activeId;
        try {
            const res = await createConversation();
            const id = res?.data?._id;
            if (!id) throw new Error("No conversation");
            setConversations((prev) => [res.data, ...prev]);
            setActiveId(id);
            return id;
        } catch {
            toast.error("Failed to start a chat");
            return null;
        }
    };

    const handleFilePick = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const convId = await ensureConversation();
        if (!convId) return;

        setUploading(true);
        try {
            const res = await uploadAttachments(convId, files);
            const newAtt = res?.data || [];
            setPendingAttachments((prev) => [...prev, ...newAtt]);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Upload failed");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemovePending = (id) => {
        setPendingAttachments((prev) => prev.filter((a) => a._id !== id));
    };

    const handleSend = async () => {
        const text = input.trim();
        if ((!text && pendingAttachments.length === 0) || streaming) return;

        const convId = await ensureConversation();
        if (!convId) return;

        // Public attachment shape for display in message bubble
        const displayAttachments = pendingAttachments.map((a) => ({
            _id: a._id,
            originalName: a.originalName,
            mimeType: a.mimeType,
            size: a.size,
            kind: a.kind
        }));

        const userMsg = {
            _id: `temp-${Date.now()}`,
            role: "user",
            content: text,
            attachments: displayAttachments,
            createdAt: new Date().toISOString()
        };
        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        const sentAttachments = pendingAttachments;
        setPendingAttachments([]);
        setStreaming(true);
        setStreamingContent("");
        setStreamingStatus("");

        let assembled = "";

        streamControllerRef.current = streamMessage(
            convId,
            text,
            { attachments: sentAttachments },
            {
                onToken: (chunk) => {
                    assembled += chunk;
                    setStreamingContent(assembled);
                },
                onStatus: (msg) => {
                    setStreamingStatus(msg);
                },
                onDone: async (meta) => {
                    const assistantMsg = {
                        _id: meta?.messageId || `temp-a-${Date.now()}`,
                        role: "assistant",
                        content: assembled,
                        attachments: meta?.attachments || [],
                        createdAt: new Date().toISOString()
                    };
                    setMessages((prev) => [...prev, assistantMsg]);
                    setStreamingContent("");
                    setStreamingStatus("");
                    setStreaming(false);
                    refreshConversations();
                    try {
                        const u = await fetchUsage();
                        setUsage(u?.data || null);
                    } catch { /* ignore */ }
                },
                onError: (msg) => {
                    const errText = msg || "Stream error";
                    toast.error(errText);
                    // Also append as a visible system-style message so user always sees it
                    const errMsg = {
                        _id: `err-${Date.now()}`,
                        role: "assistant",
                        content: `⚠️ ${errText}`,
                        createdAt: new Date().toISOString()
                    };
                    setMessages((prev) => [...prev, errMsg]);
                    setStreamingContent("");
                    setStreamingStatus("");
                    setStreaming(false);
                }
            }
        );
    };

    const handleStop = () => {
        streamControllerRef.current?.abort();
        if (streamingContent) {
            const assistantMsg = {
                _id: `temp-a-${Date.now()}`,
                role: "assistant",
                content: streamingContent + "\n\n_[stopped]_",
                createdAt: new Date().toISOString()
            };
            setMessages((prev) => [...prev, assistantMsg]);
        }
        setStreamingContent("");
        setStreaming(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // ─── Renders ─────────────────────────────────────────────────────────
    if (configLoading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-60px)]">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
            </div>
        );
    }

    if (!aiConfig?.enabled) {
        return (
            <NoticeCard
                title="AI Assistant is disabled"
                description="An administrator needs to enable the AI Assistant in System Settings."
            />
        );
    }
    if (!aiConfig?.hasApiKey) {
        return (
            <NoticeCard
                title="OpenAI API key not configured"
                description="Ask an administrator to set the OpenAI API key in System Settings → AI Assistant."
            />
        );
    }

    return (
        <div className="flex h-[calc(100vh-60px)] bg-white rounded-xl overflow-hidden border border-gray-200">
            {/* Sidebar */}
            <aside className="w-72 border-r border-gray-200 flex flex-col bg-gray-50">
                <div className="p-3 border-b border-gray-200">
                    <Button
                        onClick={handleNewChat}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        size="sm"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        New chat
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                    {conversations.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center mt-6 px-4">
                            No conversations yet. Start a new chat to begin.
                        </p>
                    ) : (
                        conversations.map((c) => {
                            const active = c._id === activeId;
                            const isEditing = editingId === c._id;
                            return (
                                <div
                                    key={c._id}
                                    onClick={() => !isEditing && setActiveId(c._id)}
                                    className={`group flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer transition-colors ${
                                        active ? "bg-purple-100 text-purple-900" : "hover:bg-gray-100 text-gray-700"
                                    }`}
                                >
                                    <MessageSquare className="h-3.5 w-3.5 flex-shrink-0" />
                                    {isEditing ? (
                                        <input
                                            autoFocus
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            onBlur={() => handleRename(c._id)}
                                            onKeyDown={(e) => e.key === "Enter" && handleRename(c._id)}
                                            className="flex-1 text-xs bg-white border border-purple-300 rounded px-1.5 py-0.5 focus:outline-none"
                                        />
                                    ) : (
                                        <span className="flex-1 text-xs truncate">{c.title || "Untitled"}</span>
                                    )}
                                    {!isEditing && (
                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingId(c._id); setEditTitle(c.title || ""); }}
                                                className="p-1 hover:bg-gray-200 rounded"
                                                title="Rename"
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(c._id, e)}
                                                className="p-1 hover:bg-red-100 hover:text-red-600 rounded"
                                                title="Delete"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {usage && (
                    <div className="border-t border-gray-200 p-3 text-[11px] text-gray-500">
                        <div className="flex items-center justify-between">
                            <span>This month</span>
                            <span className="font-medium text-gray-700">{usage.usage.total.toLocaleString()} tokens</span>
                        </div>
                        {usage.limit > 0 && (
                            <div className="mt-1.5">
                                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-purple-500"
                                        style={{ width: `${Math.min(100, (usage.usage.total / usage.limit) * 100)}%` }}
                                    />
                                </div>
                                <div className="text-[10px] text-gray-400 mt-0.5">
                                    Limit: {usage.limit.toLocaleString()}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </aside>

            {/* Chat panel */}
            <main className="flex-1 flex flex-col">
                {/* Header */}
                <header className="border-b border-gray-200 px-5 py-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    <h2 className="text-sm font-semibold text-gray-900">AI Assistant</h2>
                    <span className="text-[11px] text-gray-400 ml-1">• {aiConfig.model}</span>
                </header>

                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-b from-white to-gray-50">
                    {loadingMessages ? (
                        <div className="flex justify-center pt-10">
                            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                    ) : messages.length === 0 && !streamingContent ? (
                        <EmptyState />
                    ) : (
                        <>
                            {messages.map((m) => (
                                <Message
                                    key={m._id}
                                    role={m.role}
                                    content={m.content}
                                    attachments={m.attachments}
                                    conversationId={activeId}
                                />
                            ))}
                            {streamingContent && (
                                <Message role="assistant" content={streamingContent} streaming conversationId={activeId} />
                            )}
                            {streaming && streamingStatus && (
                                <div className="flex items-center gap-2 pl-11 text-xs text-purple-600">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    <span>{streamingStatus}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Input */}
                <div className="border-t border-gray-200 p-4 bg-white">
                    <div className="max-w-3xl mx-auto">
                        {/* Pending attachment chips */}
                        {(pendingAttachments.length > 0 || uploading) && (
                            <div className="flex flex-wrap gap-2 mb-2">
                                {pendingAttachments.map((a) => (
                                    <PendingAttachmentChip
                                        key={a._id}
                                        attachment={a}
                                        onRemove={() => handleRemovePending(a._id)}
                                    />
                                ))}
                                {uploading && (
                                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-500">
                                        <Loader2 className="h-3 w-3 animate-spin" />
                                        Uploading...
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="relative flex items-end gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFilePick}
                                className="hidden"
                                accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/csv,text/markdown,application/json,text/html,application/rtf,text/xml,application/xml"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={streaming || uploading}
                                className="h-11 w-11 flex items-center justify-center text-gray-500 hover:text-purple-600 hover:bg-gray-100 rounded-xl disabled:opacity-40 transition-colors"
                                title="Attach files (PDF, Word, images, CSV, text — max 50MB each)"
                            >
                                <Paperclip className="h-5 w-5" />
                            </button>
                            <textarea
                                ref={textareaRef}
                                rows={1}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Send a message... (Shift+Enter for new line)"
                                disabled={streaming}
                                className="flex-1 resize-none px-4 py-3 pr-12 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50"
                                style={{ maxHeight: 200 }}
                            />
                            {streaming ? (
                                <Button
                                    onClick={handleStop}
                                    className="h-11 w-11 p-0 bg-gray-700 hover:bg-gray-900 text-white rounded-xl"
                                    title="Stop"
                                >
                                    <Square className="h-4 w-4" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSend}
                                    disabled={!input.trim() && pendingAttachments.length === 0}
                                    className="h-11 w-11 p-0 bg-purple-600 hover:bg-purple-700 text-white rounded-xl disabled:bg-gray-300"
                                    title="Send"
                                >
                                    <Send className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                    <p className="text-[10px] text-gray-400 text-center mt-2">
                        AI can make mistakes. Verify important information.
                    </p>
                </div>
            </main>
        </div>
    );
}

function Message({ role, content, streaming, attachments, conversationId }) {
    const [copied, setCopied] = useState(false);
    const isUser = role === "user";

    const handleCopy = () => {
        navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const images = (attachments || []).filter((a) => a.kind === "image");
    const docs = (attachments || []).filter((a) => a.kind === "document");
    const hasAttachments = images.length > 0 || docs.length > 0;

    return (
        <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
            {!isUser && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-purple-600" />
                </div>
            )}
            <div className={`group max-w-2xl ${isUser ? "order-1" : ""}`}>
                {/* Image attachments — show above bubble */}
                {images.length > 0 && (
                    <div className={`flex flex-wrap gap-2 mb-2 ${isUser ? "justify-end" : ""}`}>
                        {images.map((img) => (
                            <InlineImage key={img._id} attachment={img} conversationId={conversationId} />
                        ))}
                    </div>
                )}
                {/* Document attachments — chip row above bubble */}
                {docs.length > 0 && (
                    <div className={`flex flex-wrap gap-1.5 mb-2 ${isUser ? "justify-end" : ""}`}>
                        {docs.map((d) => (
                            <DocumentChip key={d._id} attachment={d} conversationId={conversationId} isUser={isUser} />
                        ))}
                    </div>
                )}
                {/* Text bubble (only render if there is text or no attachments) */}
                {(content || !hasAttachments) && (
                    <div
                        className={`px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                            isUser
                                ? "bg-purple-600 text-white rounded-br-sm"
                                : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"
                        }`}
                    >
                        {content}
                        {streaming && <span className="inline-block w-1.5 h-4 ml-1 bg-purple-500 animate-pulse" />}
                    </div>
                )}
                {!isUser && !streaming && content && (
                    <button
                        onClick={handleCopy}
                        className="mt-1 ml-2 text-[11px] text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                    >
                        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        {copied ? "Copied" : "Copy"}
                    </button>
                )}
            </div>
            {isUser && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center order-2">
                    <UserIcon className="h-4 w-4 text-gray-600" />
                </div>
            )}
        </div>
    );
}

function PendingAttachmentChip({ attachment, onRemove }) {
    const Icon = getFileIcon(attachment.mimeType, attachment.kind);
    const isImage = attachment.kind === "image";
    return (
        <div className="group inline-flex items-center gap-2 pl-2 pr-1 py-1 bg-purple-50 border border-purple-200 rounded-lg max-w-xs">
            <Icon className={`h-4 w-4 flex-shrink-0 ${isImage ? "text-blue-500" : "text-purple-600"}`} />
            <div className="min-w-0">
                <div className="text-xs font-medium text-gray-800 truncate">{attachment.originalName}</div>
                <div className="text-[10px] text-gray-500">{formatBytes(attachment.size)}</div>
            </div>
            <button
                type="button"
                onClick={onRemove}
                className="ml-1 p-0.5 rounded hover:bg-purple-200 text-gray-500 hover:text-red-600"
                title="Remove"
            >
                <X className="h-3 w-3" />
            </button>
        </div>
    );
}

function DocumentChip({ attachment, conversationId, isUser }) {
    const Icon = getFileIcon(attachment.mimeType, attachment.kind);
    const handleDownload = async () => {
        try {
            const url = await fetchAttachmentBlob(conversationId, attachment._id);
            const a = document.createElement("a");
            a.href = url;
            a.download = attachment.originalName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error("Failed to download file");
        }
    };
    return (
        <button
            onClick={handleDownload}
            className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg max-w-xs transition-colors ${
                isUser
                    ? "bg-purple-500 hover:bg-purple-400 text-white border border-purple-400"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
            }`}
            title={`Download ${attachment.originalName}`}
        >
            <Icon className="h-4 w-4 flex-shrink-0" />
            <div className="min-w-0 text-left">
                <div className="text-xs font-medium truncate">{attachment.originalName}</div>
                <div className={`text-[10px] ${isUser ? "text-purple-100" : "text-gray-500"}`}>
                    {formatBytes(attachment.size)}
                </div>
            </div>
            <Download className="h-3.5 w-3.5 ml-1 flex-shrink-0 opacity-60" />
        </button>
    );
}

function InlineImage({ attachment, conversationId }) {
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let revokeUrl = null;
        let cancelled = false;
        (async () => {
            try {
                const blobUrl = await fetchAttachmentBlob(conversationId, attachment._id);
                if (!cancelled) {
                    setUrl(blobUrl);
                    revokeUrl = blobUrl;
                }
            } catch {
                if (!cancelled) setError(true);
            }
        })();
        return () => {
            cancelled = true;
            if (revokeUrl) URL.revokeObjectURL(revokeUrl);
        };
    }, [conversationId, attachment._id]);

    if (error) {
        return (
            <div className="px-3 py-6 bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-500">
                Image failed to load
            </div>
        );
    }
    if (!url) {
        return (
            <div className="w-32 h-32 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
        );
    }
    return (
        <a href={url} target="_blank" rel="noreferrer">
            <img
                src={url}
                alt={attachment.originalName}
                className="max-w-xs max-h-64 rounded-lg border border-gray-200 hover:opacity-90 transition-opacity"
            />
        </a>
    );
}

function getFileIcon(mimeType = "", kind) {
    if (kind === "image") return FileImage;
    if (mimeType.includes("pdf")) return FileText;
    if (mimeType.includes("csv") || mimeType.includes("excel") || mimeType.includes("sheet")) return FileSpreadsheet;
    if (mimeType.includes("word") || mimeType.includes("document") || mimeType.includes("text/")) return FileText;
    return FileIcon;
}

function formatBytes(bytes) {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function EmptyState() {
    const suggestions = [
        "Draft a polite follow-up message to a lead who didn't answer",
        "Summarize the key points of handling cold leads",
        "How do I handle the objection: 'I need to think about it'?",
        "Write a short professional voicemail script"
    ];
    return (
        <div className="max-w-2xl mx-auto text-center pt-12">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-purple-100 mb-4">
                <Sparkles className="h-7 w-7 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">How can I help you today?</h3>
            <p className="text-sm text-gray-500 mt-1">Ask anything about sales, scripts, or follow-ups.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-6 text-left">
                {suggestions.map((s, i) => (
                    <div
                        key={i}
                        className="px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-700 hover:border-purple-300 hover:bg-purple-50 transition-colors"
                    >
                        {s}
                    </div>
                ))}
            </div>
        </div>
    );
}

function NoticeCard({ title, description }) {
    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-md mx-auto mt-12 bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                <div className="p-3 bg-amber-50 rounded-full w-fit mx-auto mb-4">
                    <AlertCircle className="h-8 w-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
        </div>
    );
}
